import { useEffect, useState, type JSX } from 'react';

import { AlertTriangle, Loader2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

import type {
  IdentityChangeFields,
  IdentityChangePreview,
  IdentityChangePreviewUser,
} from '../types/identityChangePreview';
import {
  UsersAlertDialog as AlertDialog,
  UsersAlertDialogAction as AlertDialogAction,
  UsersAlertDialogCancel as AlertDialogCancel,
  UsersAlertDialogContent as AlertDialogContent,
  UsersAlertDialogDescription as AlertDialogDescription,
  UsersAlertDialogFooter as AlertDialogFooter,
  UsersAlertDialogHeader as AlertDialogHeader,
  UsersAlertDialogTitle as AlertDialogTitle,
} from './ui/UsersAlertDialog';

const MERGE_CONFIRM_DELAY_SECONDS = 5;

interface IdentitySharedChangeConfirmDialogProps {
  open: boolean;
  preview: IdentityChangePreview | null;
  changing: IdentityChangeFields;
  currentProjectId?: number | null;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

interface PreviewProjectGroup {
  key: string;
  projectId: number | null;
  projectName: string;
  users: IdentityChangePreviewUser[];
}

const fieldLabels = (changing: IdentityChangeFields): string => {
  const parts: string[] = [];
  if (changing.email) parts.push('correo');
  if (changing.name) parts.push('nombre');
  if (changing.password) parts.push('contraseña');
  if (parts.length === 0) return 'datos de la cuenta';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} y ${parts[1]}`;
  return `${parts[0]}, ${parts[1]} y ${parts[2]}`;
};

const projectLabel = (group: PreviewProjectGroup): string =>
  group.projectId === null ? group.projectName : `${group.projectName} (${group.projectId})`;

const userPrimary = (user: IdentityChangePreviewUser): string => user.name?.trim() || user.email;

const userSecondary = (user: IdentityChangePreviewUser): string => {
  const hasName = Boolean(user.name?.trim());
  const details = hasName ? `${user.email} · id ${user.id}` : `id ${user.id}`;
  return user.isActive ? details : `${details} (inactivo)`;
};

const groupUsersByProject = (
  users: IdentityChangePreviewUser[],
  excludeProjectId?: number | null
): PreviewProjectGroup[] => {
  const groups = new Map<string, PreviewProjectGroup>();
  users.forEach((user) => {
    if (excludeProjectId !== null && excludeProjectId !== undefined && user.projectId === excludeProjectId) return;
    const key = user.projectId === null ? 'admin' : String(user.projectId);
    const existing = groups.get(key);
    if (existing) {
      existing.users.push(user);
      return;
    }
    groups.set(key, {
      key,
      projectId: user.projectId,
      projectName: user.projectName,
      users: [user],
    });
  });
  return Array.from(groups.values());
};

const ProjectUsersList = ({ groups }: { groups: PreviewProjectGroup[] }): JSX.Element | null => {
  if (groups.length === 0) return null;

  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <div key={group.key}>
          <p className="m-0 text-sm font-medium">{projectLabel(group)}</p>
          <ul className="m-0 list-none space-y-1 p-0 text-sm">
            {group.users.map((user) => (
              <li key={user.id}>
                <span className="font-medium">{userPrimary(user)}</span>
                <span className="block text-text-subtle">{userSecondary(user)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

const MergeWarningPanel = ({
  changing,
  destinationGroups,
}: {
  changing: IdentityChangeFields;
  destinationGroups: PreviewProjectGroup[];
}): JSX.Element => (
  <div className="space-y-3">
    <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
      <AlertTriangle className="size-4" aria-hidden />
      <AlertTitle className="text-sm font-semibold">Acción irreversible</AlertTitle>
      <AlertDescription className="text-sm text-foreground/90">
        Este correo ya pertenece a otra cuenta. El usuario dejará de usar su correo actual y quedará vinculado a esa
        cuenta.
      </AlertDescription>
    </Alert>

    <div className="rounded-lg border border-warning/40 bg-warning-light px-3 py-2 text-sm text-foreground">
      <p className="m-0 font-semibold">Si confirmas:</p>
      <ul className="mt-1.5 mb-0 list-disc space-y-1 pl-5">
        <li>Usará la contraseña y las credenciales de la cuenta de destino.</li>
        <li>Podrá entrar a todos los proyectos asociados al mail</li>
        <li>Solo cambia este usuario; los demás accesos con el correo anterior no se modifican.</li>
        <li className="font-medium text-destructive">
          Los proyectos seleccionados en este formulario no se aplicarán.
        </li>
        {changing.password ? (
          <li className="font-medium text-destructive">La contraseña que escribiste no se aplicará.</li>
        ) : null}
        {changing.name ? (
          <li className="font-medium text-destructive">El nombre que escribiste no se aplicará.</li>
        ) : null}
      </ul>
    </div>

    <div>
      <p className="m-0 text-sm font-semibold text-foreground">
        Cuenta de destino — proyectos a los que ganará acceso:
      </p>
      <div className="mt-1.5 rounded-lg border border-border-subtle bg-muted/30 px-3 py-2">
        <ProjectUsersList groups={destinationGroups} />
      </div>
    </div>
  </div>
);

const IdentitySharedChangeConfirmDialog = ({
  open,
  preview,
  changing,
  currentProjectId,
  isLoading,
  onClose,
  onConfirm,
}: IdentitySharedChangeConfirmDialogProps): JSX.Element | null => {
  const [mergeConfirmSecondsLeft, setMergeConfirmSecondsLeft] = useState(MERGE_CONFIRM_DELAY_SECONDS);

  const isMerge = preview?.action === 'merged';
  const fieldsText = fieldLabels(changing);
  const groups = groupUsersByProject(
    isMerge ? preview?.destinationUsers ?? [] : preview?.currentUsers ?? [],
    isMerge ? currentProjectId : undefined
  );

  useEffect(() => {
    if (!open || !isMerge) {
      setMergeConfirmSecondsLeft(MERGE_CONFIRM_DELAY_SECONDS);
      return undefined;
    }

    setMergeConfirmSecondsLeft(MERGE_CONFIRM_DELAY_SECONDS);
    const intervalId = window.setInterval(() => {
      setMergeConfirmSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return (): void => {
      window.clearInterval(intervalId);
    };
  }, [open, isMerge, preview?.action]);

  if (!preview) return null;

  const mergeConfirmLocked = isMerge && mergeConfirmSecondsLeft > 0;
  const confirmDisabled = isLoading || mergeConfirmLocked;

  const confirmLabel = ((): string => {
    if (isLoading) return 'Confirmando…';
    if (mergeConfirmLocked) {
      return `Confirmar en ${mergeConfirmSecondsLeft}s`;
    }
    return isMerge ? 'Sí, mover a la otra cuenta' : 'Confirmar';
  })();

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        className={
          isMerge
            ? 'sm:!max-w-lg max-h-[80vh] overflow-hidden border-2 border-destructive/30 grid-rows-[auto_minmax(0,1fr)_auto]'
            : 'sm:!max-w-md'
        }
      >
        <AlertDialogHeader>
          <AlertDialogTitle className={isMerge ? 'text-destructive' : undefined}>
            {isMerge ? 'Advertencia: este correo ya está en uso' : 'Este cambio afecta a toda la cuenta'}
          </AlertDialogTitle>
          {!isMerge ? (
            <AlertDialogDescription>
              {`El cambio de ${fieldsText} se comparte entre todos los usuarios de esta cuenta. Al confirmar, se actualizará para:`}
            </AlertDialogDescription>
          ) : (
            <AlertDialogDescription>
              Lee con atención. El botón de confirmación se habilitará en {MERGE_CONFIRM_DELAY_SECONDS} segundos.
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>

        {isMerge ? (
          <div className="min-h-0 overflow-y-auto pr-1">
            <MergeWarningPanel changing={changing} destinationGroups={groups} />
          </div>
        ) : (
          <ProjectUsersList groups={groups} />
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading} onClick={onClose}>
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={confirmDisabled}
            className={isMerge ? 'gap-2 bg-destructive hover:bg-destructive/90' : 'gap-2'}
            onClick={onConfirm}
          >
            {isLoading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default IdentitySharedChangeConfirmDialog;
